import React, { useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { Plating } from 'src/components/3d/Plating';
import { Stele } from 'src/components/3d/Stele';
import { Sole } from 'src/components/3d/Sole';
import { BoxCustom } from 'src/components/3d/BoxCustom';
import { Piece as PieceComponent } from '../components/3d/Piece';
import { Vector3, WebGLRenderer } from 'three';
import * as THREE from 'three';
import type { StateNames, States } from './Configurator';
import type { QuoteStep } from './project/components/NavBar';

type MonumentProps = {
  selectedPoints: { pieceIndex: number; point: Float32Array }[] | null;
  pieces: THREE.Object3D<THREE.Event>[];
  pieceIndex: number;
  reloadPiece: boolean;
  customSteles: any[];
  customPlatings: any[];
  orbitRef: React.MutableRefObject<any>;
  activePieces: any[];
  setSelectedPoints: (
    seletedPoints: { pieceIndex: number; point: Float32Array }[] | null,
  ) => void;
  setPieceIndex: (pieceIndex: number) => void;
  setReloadPiece: (reloadPiece: boolean) => void;
  changePosition: (newPosition: Vector3) => void;
  setCustomSteles: (customSteles: any[]) => void;
  setCustomPlatings: (customPlatings: any[]) => void;
  setValue: <T extends StateNames>(stateName: T, value: States[T]) => void;
  getValue: (stateName: StateNames) => States[StateNames];
  checkCollision: (
    pieceIndexToCheck: number,
    axe: 'x' | 'y' | 'z',
    forceBox1?: {
      checkOnlySelectedPiece?: boolean;
      box1Geometry?: any[];
      box1Position?: { x: number; y: number; z: number };
    },
  ) => void;
  setGl: (gl: WebGLRenderer) => void;
  step: QuoteStep;
  patterns: {
    [key: string]: { [key: string]: any }[];
  };
  selectedPattern: number | undefined;
  orientation: {
    key: 'x' | 'y' | 'z' | 'free';
    inverse: boolean;
  };
  centerCamera: () => void;
  setCustomSoles: (customSoles: any[]) => void;
  customSoles: any[];
  setCustomBoxes: (customBoxes: any[]) => void;
  customBoxes: any[];
};

export const Monument = ({
  selectedPoints,
  pieces,
  pieceIndex,
  reloadPiece,
  customSteles,
  customPlatings,
  orbitRef,
  activePieces,
  setSelectedPoints,
  setPieceIndex,
  setReloadPiece,
  changePosition,
  setCustomSteles,
  setCustomPlatings,
  setValue,
  getValue,
  checkCollision,
  setGl,
  step,
  selectedPattern,
  patterns,
  orientation,
  centerCamera,
  setCustomSoles,
  customSoles,
  setCustomBoxes,
  customBoxes,
}: MonumentProps) => {
  const gl = useThree((state) => state.gl);

  useEffect(() => {
    setGl(gl);
  }, [setGl, gl]);

  useEffect(() => {
    centerCamera();
  }, [orbitRef, pieces, reloadPiece, centerCamera]);

  useEffect(() => {
    setPieceIndex(pieceIndex);
  }, [pieceIndex, setPieceIndex]);

  if (reloadPiece) {
    setReloadPiece(false);
    return <></>;
  }

  const enablePan = getValue('advancedMove') ?? false;

  return (
    <>
      <OrbitControls
        ref={orbitRef}
        minPolarAngle={0}
        maxPolarAngle={Math.PI}
        enablePan={enablePan as boolean}
        onClick={() => {}}
        enableDamping={false}
        dampingFactor={0}
      />

      {pieces.map((el: any, elIndex: number) => {
        return (
          <PieceComponent
            key={elIndex}
            orbitRef={orbitRef}
            indexPiece3D={elIndex}
            pieceIndex={pieceIndex}
            setPieceIndex={setPieceIndex}
            piece={el}
            reloadPiece={reloadPiece}
            selectedPoints={selectedPoints}
            setSelectedPoints={setSelectedPoints}
            changePosition={changePosition}
            setValue={setValue}
            getValue={getValue}
            activePieces={activePieces}
            setReloadPiece={setReloadPiece}
            pieces={pieces}
            checkCollision={checkCollision}
            step={step}
            selectedPattern={selectedPattern}
            patterns={patterns}
            orientation={orientation}
          />
        );
      })}

      {customSteles.map((el, elIndex) => {
        return (
          <Stele
            key={`stele_${pieceIndex + elIndex}`}
            orbitRef={orbitRef}
            pieceIndex={pieces.length + elIndex}
            setPieceIndex={setPieceIndex}
            index={pieceIndex}
            customStele={el}
            setCustomSteles={setCustomSteles}
            customSteles={customSteles}
            setValue={setValue}
            getValue={getValue}
            step={step}
            pieces={pieces}
            patterns={patterns}
            orientation={orientation}
            customPlatings={customPlatings}
            customSoles={customSoles}
          />
        );
      })}

      {customPlatings.map((el, elIndex) => {
        if (!el.isActive) return null;
        return (
          <Plating
            key={`plating_${pieceIndex + elIndex}`}
            orbitRef={orbitRef}
            customPlating={el}
            pieceIndex={pieces.length + customSteles.length + elIndex}
            setPieceIndex={setPieceIndex}
            index={pieceIndex}
            setCustomPlatings={setCustomPlatings}
            customPlatings={customPlatings}
            getValue={getValue}
            step={step}
          />
        );
      })}

      {customSoles.map((el, elIndex) => {
        if (!el.isActive) return null;
        return (
          <Sole
            key={`sole_${pieceIndex + elIndex}`}
            orbitRef={orbitRef}
            customSole={el}
            pieceIndex={
              pieces.length +
              customSteles.length +
              customPlatings.length +
              elIndex
            }
            setPieceIndex={setPieceIndex}
            index={pieceIndex}
            setCustomSoles={setCustomSoles}
            customSoles={customSoles}
            getValue={getValue}
            step={step}
          />
        );
      })}

      {customBoxes.map((el, elIndex) => {
        if (!el.isActive) return null;
        return (
          <BoxCustom
            key={`box_${pieceIndex + elIndex}`}
            orbitRef={orbitRef}
            customBox={el}
            pieceIndex={
              pieces.length +
              customSteles.length +
              customPlatings.length +
              customSoles.length +
              elIndex
            }
            setPieceIndex={setPieceIndex}
            index={pieceIndex}
            setCustomBoxes={setCustomBoxes}
            customBoxes={customBoxes}
            getValue={getValue}
            step={step}
          />
        );
      })}
    </>
  );
};
